<template>
  <v-ons-page>
    <div class="page__background" style="background-color: white" />
    <div class="stream-group-wrapper">
      <common-header>
        <template #header>
          <Button modifier="quiet" class="backButton" @click="router.push('/ja/home')">
            <v-ons-icon icon="md-chevron-left" />
          </Button>
          <span>配信グループの管理</span>
          <button class="reload-button" @click="clickReloadButton">
            <v-ons-icon icon="fa-rotate-right" size="2rem" />
          </button>
        </template>
      </common-header>
      <ja-stream-group
        v-show="displayState === DISPLAYSTATE.LOADING_DONE"
        class="stream-groups"
        :streamingStreamGroup="streamingStreamGroup"
        :notStreamingStreamGroups="notStreamingStreamGroups"
        :scheduledStreamGroups="scheduledStreamGroups"
        :getStreamGroup="getStreamGroup"
        :openModal="openModal"
      />
      <progress-circular v-show="displayState === DISPLAYSTATE.LOADING_PROGRESS">
      </progress-circular>
      <v-ons-modal :visible="isModalVisible" class="modal-wrapper p-fixed">
        <div>
          <ja-stream-group-streaming-type-modal
            v-if="modalPageNum === MODAL_PAGE_NUM_LIST.STREAMING_TYPE"
            :selectedStreamGroupId="selectedStreamGroupId"
            :closeModal="closeModal"
            :getStreamGroup="getStreamGroup"
            :moveSchedulingPage="moveSchedulingPage"
          />
          <ja-stream-group-scheduling-modal
            v-if="modalPageNum === MODAL_PAGE_NUM_LIST.SCHEDULING"
            :getStreamGroup="getStreamGroup"
            :selectedStreamGroupId="selectedStreamGroupId"
            :closeModal="closeModal"
          />
        </div>
      </v-ons-modal>

      <common-footer class="footer">
        <template #footer>
          <div class="addButton">
            <small-button
              title="配信グループを作成する"
              class="smallButton"
              @click="moveContentSelect"
            />
          </div>
        </template>
      </common-footer>
    </div>
  </v-ons-page>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import SmallButton from '@/components/common/SmallButton.vue';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import JaStreamGroup from '@/components/JaStreamGroup/JaStreamGroup.vue';
import JaStreamGroupStreamingTypeModal from '@/components/JaStreamGroup/JaStreamGroupStreamingTypeModal.vue';
import JaStreamGroupSchedulingModal from '@/components/JaStreamGroup/JaStreamGroupSchedulingModal.vue';
import Button from '@/components/common/Button.vue';
import { deleteFailureUploadStreamGroups } from '@/composable/data_push_common';
import { dataFetchStreamGroups, dataFetchUpdateStatus } from '@/composable/data_fetch_common';
import { stopPolling } from '@/composable/material_common';

const router = useRouter();
const store = useStore();

// 配信中配信グループ
const streamingStreamGroup = ref({});

// 未配信配信グループ
const notStreamingStreamGroups = ref([]);

// 配信予約済配信グループ
const scheduledStreamGroups = ref([]);

// 登録中配信グループ
const uploadingStreamGroupIdList = ref([]);

// ローディング完了フラグ
const isLoadingDone = ref(false);

// ローディングステータス
const DISPLAYSTATE = {
  LOADING_PROGRESS: 'ロード中',
  LOADING_DONE: 'ロード完了',
};

/**
 * 画面表示状態
 */
const displayState = computed(() => {
  return isLoadingDone.value ? DISPLAYSTATE.LOADING_DONE : DISPLAYSTATE.LOADING_PROGRESS;
});
// 配信種別・予約設定モーダル可視状態
const isModalVisible = ref(false);
// モーダルページ番号
const modalPageNum = ref(1);
// モーダルページ番号リスト
const MODAL_PAGE_NUM_LIST = {
  STREAMING_TYPE: 1,
  SCHEDULING: 2,
};
// 配信ボタンが押された配信グループのID
const selectedStreamGroupId = ref(0);
// Intervalオブジェクト
const pollingInterval = ref(null);

/**
 * コンテンツ選択画面遷移処理
 */
const moveContentSelect = () => {
  store.commit('streamGroup/setIsStreamGroupUpdate', 0);
  store.commit('streamGroup/setStreamGroupId', 0);
  store.commit('streamGroup/setStreamGroupName', '');
  store.commit('streamGroup/setIsStreaming', 0);
  store.commit('streamGroup/setStreamGroupContentList', []);
  router.push('/ja/streamGroup/contentSelect');
};
/**
 * 配信グループ一覧取得および配信中・未配信分類処理
 */
const getStreamGroup = async () => {
  isLoadingDone.value = false;
  const streamGroups = await dataFetchStreamGroups(store);
  streamingStreamGroup.value = streamGroups.streamingStreamGroupList?.[0] ?? {};
  notStreamingStreamGroups.value = streamGroups.notStreamingStreamGroupList ?? [];
  scheduledStreamGroups.value = streamGroups.scheduledStreamGroupList ?? [];

  // 登録中配信グループIDリストを取得する
  uploadingStreamGroupIdList.value = await dataFetchUpdateStatus(store);
  isLoadingDone.value = true;
};

/**
 * ポーリング処理
 */
const startPolling = async () => {
  // 登録中配信グループIDリストを取得する
  uploadingStreamGroupIdList.value = await dataFetchUpdateStatus(store);
};

/**
 * 配信予約設定モーダルに遷移する
 */
const moveSchedulingPage = () => {
  modalPageNum.value = MODAL_PAGE_NUM_LIST.SCHEDULING;
};

/**
 * 配信種別・予約設定モーダルを表示する
 */
const openModal = (groupId) => {
  selectedStreamGroupId.value = groupId;
  modalPageNum.value = MODAL_PAGE_NUM_LIST.STREAMING_TYPE;
  isModalVisible.value = true;
};

/**
 * 配信種別・予約設定モーダルを閉じる
 */
const closeModal = () => {
  isModalVisible.value = false;
};

/**
 * アップロードに失敗した配信グループを削除
 */
const deleteFailureStreamGroups = async () => {
  // ポーリング停止
  stopPolling(pollingInterval.value);
  // アップロードエラーデータ削除
  await deleteFailureUploadStreamGroups();
};

const clickReloadButton = async () => {
  await deleteFailureStreamGroups();
  getStreamGroup();
};

const disableScroll = () => {
  document.body.style.overflow = 'hidden';
};
const enableScroll = () => {
  document.body.style.overflow = '';
};

onMounted(() => {
  getStreamGroup();
  window.addEventListener('beforeunload', deleteFailureStreamGroups);
});
onBeforeUnmount(() => {
  deleteFailureStreamGroups();
  window.removeEventListener('beforeunload', deleteFailureStreamGroups);
});

/**
 * モーダル表示状態を監視し、スクロール可否を制御する
 * @param {*} isModalVisible モーダル表示状態
 */
watch(isModalVisible, (val) => {
  if (val) {
    disableScroll();
  } else {
    enableScroll();
  }
});

watch(
  /**
   * 登録中配信グループIDリストを監視し、ポーリング&再描画判定を行う
   * @param {*} newValue 最新の登録中配信グループIDリスト
   * @param {*} oldValue 1世代前の登録中配信グループIDリスト
   */
  uploadingStreamGroupIdList,
  (newValue, oldValue) => {
    if (!oldValue || oldValue.length === undefined) return;

    if (oldValue.length !== 0 && newValue.length === 0) {
      // 再描画&ポーリング停止
      getStreamGroup();
      stopPolling(pollingInterval.value);
    } else if (!newValue.every((value, idx) => value === oldValue[idx])) {
      // 登録中配信グループIDリストに変化があったら再描画
      getStreamGroup();
    } else if (newValue.length !== 0) {
      // 最新の登録中配信グループIDリストが空で無い場合、ポーリング要否を確認
      if (pollingInterval.value == null) {
        // 3秒ごとに実行
        pollingInterval.value = setInterval(startPolling, 3000);
      }
    }
  },
  { deep: true }
);
</script>

<style scoped>
.stream-group-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}
/** 配信グループリスト */
.stream-groups {
  max-width: 840px;
  width: 100%;
  padding-top: 10vh;
  padding-top: 10dvh;
  padding-bottom: 10vh;
  padding-bottom: 10dvh;
}

/** ボタン */
.backButton {
  background: transparent;
  border: #fff;
  color: #aaaaaa;
  font-size: 4rem;
  left: 2rem;
  position: absolute;
}

.addButton {
  background: #21963b;
  box-shadow: 0rem 0.2rem #13822c;
  border-radius: 10px;
  color: #fff;
  font-size: 2rem;
  font-weight: bold;
  height: 60%;
  position: absolute;
  text-align: center;
  width: 80%;
  padding: 0;
}
.addButton:hover {
  opacity: 0.8;
}
.smallButton {
  height: 100%;
  padding: 0;
  cursor: pointer;
}

/** モーダルラッパー */
.modal-wrapper {
  height: 100vh;
  height: 100dvh;
}
/** 配信グループリロードボタン */
.reload-button {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.5rem;
  width: 2.5rem;
  border-radius: 50%;
  border: none;
  background-color: inherit;
  color: #444;
  opacity: 0.4;
  right: 2rem;
  position: absolute;
}
.footer {
  height: 9rem;
  flex-direction: column;
}
</style>
